/**
 * Section DnD utilities — §19 section drag with children.
 *
 * Sections use a **positional model**: a section's "children" are all items
 * after it (by `sortIndex`) until the next section or end of the container/group.
 *
 * This module provides:
 *   - `getSectionChildren` — pure positional lookup
 *   - `getSectionBoundaryIds` — valid snap targets for section drops
 *   - `isSectionItem` — type guard
 */

import { ObjectType, type DbObject } from "@/db/schema"
import type { ViewItem } from "@/features/views/hooks/useViewData"

// ---------------------------------------------------------------------------
// Type guard
// ---------------------------------------------------------------------------

/** Check if a ViewItem (or DbObject) is a Section */
export function isSectionItem(item: ViewItem): boolean {
  return item.task.type === ObjectType.Section
}

export function isSectionObject(obj: DbObject): boolean {
  return obj.type === ObjectType.Section
}

// ---------------------------------------------------------------------------
// getSectionChildren
// ---------------------------------------------------------------------------

/**
 * Get the positional children of a section within a flat, sorted item list.
 *
 * Children are all items AFTER the section, up to (but not including) the
 * next section or the end of the list.
 *
 * @param sectionId - The ID of the section
 * @param items - Flat sorted items (by sortIndex/totodo.index)
 * @returns Array of child ViewItems (may be empty)
 */
export function getSectionChildren(
  sectionId: string,
  items: ViewItem[],
): ViewItem[] {
  const sectionIdx = items.findIndex((vi) => vi.task.id === sectionId)
  if (sectionIdx === -1) return []

  const children: ViewItem[] = []
  for (let i = sectionIdx + 1; i < items.length; i++) {
    if (items[i]!.task.type === ObjectType.Section) break
    children.push(items[i]!)
  }
  return children
}

/**
 * Get the section ID + all its child IDs as a single array.
 * Used in handleDragStart to expand a section drag into multi-drag.
 *
 * @returns [sectionId, ...childIds] in display order
 */
export function getSectionBlockIds(
  sectionId: string,
  items: ViewItem[],
): string[] {
  const children = getSectionChildren(sectionId, items)
  return [sectionId, ...children.map((c) => c.task.id!).filter(Boolean)]
}

// ---------------------------------------------------------------------------
// Section boundary snap targets
// ---------------------------------------------------------------------------

/**
 * Compute valid drop positions for a section block (section-boundary snapping).
 *
 * Valid positions are:
 *   1. Before the first item (top of list/group)
 *   2. Before each section (swap sections)
 *   3. After the last item (bottom of list/group)
 *
 * Returns IDs that should remain as collision candidates during section drag.
 * All other item IDs should be filtered from collision detection.
 *
 * @param items - Flat sorted items within the same container/group
 * @param draggedIds - IDs being dragged (section + children), excluded from results
 * @returns Set of valid collision target IDs
 */
export function getSectionBoundaryIds(
  items: ViewItem[],
  draggedIds: Set<string>,
): Set<string> {
  const validIds = new Set<string>()

  // Filter out dragged items first
  const remaining = items.filter((vi) => !draggedIds.has(vi.task.id ?? ""))
  if (remaining.length === 0) return validIds

  // 1. First item = top of list (drop before it → becomes first)
  const firstId = remaining[0]!.task.id
  if (firstId) validIds.add(firstId)

  // 2. Each section in remaining items
  for (const vi of remaining) {
    if (vi.task.type === ObjectType.Section && vi.task.id) {
      validIds.add(vi.task.id)
    }
  }

  // 3. Last item = bottom of list (drop after it → becomes last)
  const lastId = remaining[remaining.length - 1]!.task.id
  if (lastId) validIds.add(lastId)

  return validIds
}
